
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Settings, CheckCircle, AlertCircle } from 'lucide-react';
import { UserRole } from '@/types/user';
import { validateProfileCompletion, getFieldLabel } from '@/services/profileValidationService';

interface ProfileCompletionNoticeProps {
  userRole: UserRole;
  profile: any;
  onProfileNavigation: () => void;
}

const ProfileCompletionNotice: React.FC<ProfileCompletionNoticeProps> = ({
  userRole,
  profile,
  onProfileNavigation
}) => {
  const validation = validateProfileCompletion(profile, userRole);
  
  if (validation.isComplete) {
    return (
      <Card className="bg-green-900/20 border border-green-500">
        <CardHeader className="pb-3">
          <CardTitle className="text-green-400 flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Profile Complete
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-green-300 mb-2">
            Your {userRole.replace('-', ' ')} profile is complete! ({validation.completionPercentage}%)
          </p>
          <p className="text-green-200 text-sm">
            {validation.roleSpecificMessage}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-blue-900/20 border border-blue-500">
      <CardHeader className="pb-3">
        <CardTitle className="text-blue-400 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          Complete Your Profile ({validation.completionPercentage}%)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-blue-300 mb-3">
          {validation.roleSpecificMessage}
        </p>
        
        {validation.missingRequiredFields.length > 0 && (
          <div className="mb-4">
            <p className="text-red-300 text-sm font-medium mb-2">Required fields:</p>
            <div className="flex flex-wrap gap-1">
              {validation.missingRequiredFields.map((field) => (
                <span key={field} className="bg-red-900/30 text-red-200 px-2 py-1 rounded text-xs">
                  {getFieldLabel(field)}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {validation.missingRecommendedFields.length > 0 && (
          <div className="mb-4">
            <p className="text-yellow-300 text-sm font-medium mb-2">Recommended fields:</p>
            <div className="flex flex-wrap gap-1">
              {validation.missingRecommendedFields.slice(0, 5).map((field) => (
                <span key={field} className="bg-yellow-900/30 text-yellow-200 px-2 py-1 rounded text-xs">
                  {getFieldLabel(field)}
                </span>
              ))}
              {validation.missingRecommendedFields.length > 5 && (
                <span className="text-yellow-300 text-xs px-2 py-1">
                  +{validation.missingRecommendedFields.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
        
        <Button
          onClick={onProfileNavigation}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Complete Profile Now (+50 points when finished)
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProfileCompletionNotice;
